import { useEffect, useState } from 'react'
import {
  Accordion, AccordionDetails, AccordionSummary,
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Grid, InputAdornment, MenuItem, Stack, Switch, TextField, Typography,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/ErrorOutline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useDispatch, useSelector } from 'react-redux'
import {
  createAdminConfig, updateAdminConfig, validateAdminSql, clearValidation,
  selectAdminValidation, selectAdminSaving,
} from '../../reducers/Beacon4PerformanceAdminReducer'

const TECHS       = ['2G', '3G', '4G', '5G']
const CATEGORIES  = ['Main KPI', 'Breakdown', 'Breakdown2']
const UNITS       = ['%', '#', 'erl', 'MB']
const FORMULAS    = ['SIMPLE', 'COMPLEX']

const EMPTY = {
  tech: '2G', category: 'Main KPI', kpiKey: '', kpiName: '',
  unit: '%', isRate: false, slaGreen: null, slaRed: null,
  displayOrder: 0, isActive: true,
  viewName: '', formulaType: 'SIMPLE', sqlExpression: '',
  dateCol: 'Date', polygonCol: 'Polygon4', jvidCol: 'JV_ID',
  mycomCol: 'Mycom_id', vendorCol: 'VENDOR',
  note: '',
}

// Compact eyebrow header for a form section.
function SectionLabel({ children }) {
  return (
    <Typography sx={{
      fontSize: 10.5, fontWeight: 700, color: 'text.secondary',
      textTransform: 'uppercase', letterSpacing: '0.08em',
      mb: 1.25, mt: 0.5,
    }}>
      {children}
    </Typography>
  )
}

/**
 * Create, edit, or clone a KPI's business metadata.
 *
 * Three modes, resolved from the props:
 *   - `config` set        → Edit an existing KPI (kpiKey locked).
 *   - `cloneSource` set    → Create a NEW KPI pre-filled from an existing one
 *                            (same as Create under the hood — it still goes
 *                            through createAdminConfig — just with the form
 *                            seeded from cloneSource instead of blank).
 *   - neither set          → Plain "New KPI", blank form.
 *
 * The Data source + formula accordion is collapsed by default for a blank
 * create (nothing to show yet), but starts OPEN when cloning, since it's
 * already filled with the source KPI's real formula and hiding that would
 * make the clone look emptier than it is.
 */
export default function Beacon4PerformanceAdminMetadataDialog({ open, config, cloneSource, onClose, onSaved }) {
  const dispatch   = useDispatch()
  const validation = useSelector(selectAdminValidation)
  const saving     = useSelector(selectAdminSaving)

  const isNew    = !config
  const isClone  = isNew && !!cloneSource
  const [form, setForm]         = useState(EMPTY)
  const [sqlDirty, setSqlDirty] = useState(false)
  const [advOpen, setAdvOpen]   = useState(false)

  useEffect(() => {
    if (!open) return
    if (config) {
      setForm({ ...EMPTY, ...config, note: '' })
    } else if (cloneSource) {
      // Carry over everything except identity — a clone is still a brand
      // new record, so kpiKey (permanent once saved) and the display name
      // must be deliberately chosen, not silently duplicated.
      setForm({
        ...EMPTY, ...cloneSource,
        kpiKey:  `${cloneSource.kpiKey}_copy`,
        kpiName: `${cloneSource.kpiName} (Copy)`,
        note: '',
      })
    } else {
      setForm(EMPTY)
    }
    setSqlDirty(false)
    setAdvOpen(isClone)
    dispatch(clearValidation())
  }, [open, config, cloneSource, dispatch]) // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (e) => {
    const v = e?.target?.type === 'checkbox' ? e.target.checked : e.target.value
    setForm(f => ({ ...f, [k]: v }))
  }
  const setNum = (k) => (e) => {
    const raw = e.target.value
    setForm(f => ({ ...f, [k]: raw === '' ? null : Number(raw) }))
  }
  const setSourceField = (k) => (e) => {
    set(k)(e)
    setSqlDirty(true)
    dispatch(clearValidation())
  }

  const onValidate = () => {
    dispatch(validateAdminSql({ viewName: form.viewName, sqlExpression: form.sqlExpression }))
    setSqlDirty(false)
  }

  const requiredOk =
    !!form.tech && !!form.kpiKey.trim() && !!form.kpiName.trim() &&
    (!isNew || !!form.viewName.trim())

  // A clone starts with a formula that was already valid on its source KPI,
  // so don't force a re-Validate click before the user has touched anything —
  // only require it once they've actually edited the SQL/view (sqlDirty).
  const sqlOk = !isNew
    ? true
    : (!form.sqlExpression.trim() || (isClone && !sqlDirty) || (!!validation?.valid && !sqlDirty))

  const canSave = requiredOk && sqlOk && !saving

  // If user tries to save but Advanced is required & invalid, open it for them.
  const onSave = async () => {
    if (isNew && !form.viewName.trim()) { setAdvOpen(true); return }
    const { id, ...dto } = form
    if (isNew) await dispatch(createAdminConfig(dto))
    else       await dispatch(updateAdminConfig({ id: config.id, dto }))
    onSaved?.(dto)
    onClose()
  }

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 1.5 } }}>
      <DialogTitle sx={{ fontSize: 15, fontWeight: 600, pb: 1 }}>
        {isClone ? 'Clone KPI' : (isNew ? 'New KPI' : 'Edit KPI')}
        {!isNew && (
          <Typography component="span" sx={{ ml: 1, fontSize: 12, fontWeight: 400, color: 'text.secondary' }}>
            {config?.kpiName} · {config?.kpiKey}
          </Typography>
        )}
        {isClone && (
          <Typography component="span" sx={{ ml: 1, fontSize: 12, fontWeight: 400, color: 'text.secondary' }}>
            from {cloneSource?.kpiName} · {cloneSource?.kpiKey}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers sx={{ px: 3, py: 2.5 }}>
        {isClone && (
          <Alert severity="info" sx={{ mb: 2.5, fontSize: 11.5 }}>
            Formula, presentation and column mapping were copied from <strong>{cloneSource?.kpiKey}</strong>.
            Pick a unique KPI key and name below, then adjust anything else the clone needs.
          </Alert>
        )}

        {/* ───────────── Identity ───────────── */}
        <SectionLabel>Identity</SectionLabel>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <TextField select fullWidth size="small" label="Tech" value={form.tech} onChange={set('tech')}>
              {TECHS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField select fullWidth size="small" label="Category" value={form.category} onChange={set('category')}>
              {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="KPI key" value={form.kpiKey} onChange={set('kpiKey')}
              placeholder="vssr" disabled={!isNew}
              helperText={isNew ? 'Stable identifier — lowercase, no spaces. Cannot be changed later.' : 'Identifier is permanent.'}
              FormHelperTextProps={{ sx: { fontSize: 10 } }} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Display name" value={form.kpiName} onChange={set('kpiName')}
              placeholder="e.g. Voice Setup Success Rate" />
          </Grid>
        </Grid>

        {/* ───────────── Presentation ───────────── */}
        <Box sx={{ mt: 3 }}>
          <SectionLabel>Presentation</SectionLabel>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6} sm={3}>
              <TextField select fullWidth size="small" label="Unit" value={form.unit} onChange={set('unit')}>
                {UNITS.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" type="number" label="SLA green"
                value={form.slaGreen ?? ''} onChange={setNum('slaGreen')}
                InputProps={{ endAdornment: <InputAdornment position="end" sx={{ '& p': { fontSize: 11 } }}>{form.unit}</InputAdornment> }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" type="number" label="SLA red"
                value={form.slaRed ?? ''} onChange={setNum('slaRed')}
                InputProps={{ endAdornment: <InputAdornment position="end" sx={{ '& p': { fontSize: 11 } }}>{form.unit}</InputAdornment> }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" type="number" label="Display order"
                value={form.displayOrder} onChange={setNum('displayOrder')} />
            </Grid>
            {/* Chart style used to live here, per-KPI. It's now a property of
                the breakdown mapping instead (Manage KPIs > Breakdowns), so
                the same KPI can be a plain line in one chart and a shaded
                area in another, depending on where it's used. */}
            <Grid item xs={12}>
              <Stack direction="row" spacing={2}>
                <FormControlLabel
                  control={<Switch checked={form.isRate} onChange={set('isRate')} size="small" />}
                  label={<Typography sx={{ fontSize: 12 }}>Lower is better</Typography>}
                />
                <FormControlLabel
                  control={<Switch checked={form.isActive} onChange={set('isActive')} size="small" />}
                  label={<Typography sx={{ fontSize: 12 }}>Active</Typography>}
                />
              </Stack>
            </Grid>
          </Grid>
        </Box>

        {/* ───────────── Advanced (create only) ───────────── */}
        {isNew && (
          <Accordion
            expanded={advOpen}
            onChange={(_, v) => setAdvOpen(v)}
            disableGutters elevation={0}
            sx={{
              mt: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1,
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44, '& .MuiAccordionSummary-content': { my: 0 } }}>
              <Box>
                <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>Data source &amp; formula</Typography>
                <Typography sx={{ fontSize: 10.5, color: 'text.disabled' }}>
                  Required — which SQL view feeds this KPI and how its value is computed
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Source view"
                    value={form.viewName} onChange={setSourceField('viewName')}
                    placeholder="VTK_S12_2G_DATA"
                    sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace' } }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField select fullWidth size="small" label="Formula type"
                    value={form.formulaType} onChange={set('formulaType')}>
                    {FORMULAS.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <Typography sx={{ fontSize: 10.5, fontWeight: 600, color: 'text.secondary', mb: 0.5, mt: 1 }}>
                    Column mapping
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField fullWidth size="small" label="Date column" value={form.dateCol} onChange={set('dateCol')}
                    sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace' } }} />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField fullWidth size="small" label="Polygon column" value={form.polygonCol} onChange={set('polygonCol')}
                    sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace' } }} />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField fullWidth size="small" label="Site (JV ID) column" value={form.jvidCol ?? ''} onChange={set('jvidCol')}
                    sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace' } }} />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField fullWidth size="small" label="Mycom column" value={form.mycomCol ?? ''} onChange={set('mycomCol')}
                    sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace' } }} />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField fullWidth size="small" label="Vendor column" value={form.vendorCol ?? ''} onChange={set('vendorCol')}
                    sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace' } }} />
                </Grid>

                <Grid item xs={12}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1, mb: 0.5 }}>
                    <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: 'text.secondary', flex: 1 }}>
                      SQL expression
                    </Typography>
                    <Button size="small" variant="outlined" onClick={onValidate}
                      disabled={!form.viewName.trim() || !form.sqlExpression.trim()}>
                      Validate
                    </Button>
                  </Stack>
                  <TextField fullWidth multiline minRows={4} maxRows={10}
                    value={form.sqlExpression} onChange={setSourceField('sqlExpression')}
                    placeholder="100.0 * SUM(ISNULL(NUM,0)) / NULLIF(SUM(ISNULL(DEN,0)),0)"
                    sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: 11.5 } }} />
                  {validation && <ValidationAlert validation={validation} />}
                  {!!form.sqlExpression.trim() && (sqlDirty || !validation?.valid) && (
                    <Typography sx={{ fontSize: 10.5, color: 'warning.main', mt: 0.75 }}>
                      Run Validate before creating to confirm the SQL parses against the source view.
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        )}

        {/* ───────────── Change note ───────────── */}
        <Box sx={{ mt: 3 }}>
          <SectionLabel>Audit</SectionLabel>
          <TextField fullWidth size="small" label="Change note"
            value={form.note ?? ''} onChange={set('note')}
            placeholder="Optional — short description of what you changed and why" />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={onSave} disabled={!canSave} sx={{ minWidth: 130 }}>
          {saving ? 'Saving…' : (isClone ? 'Create clone' : (isNew ? 'Create KPI' : 'Save changes'))}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function ValidationAlert({ validation }) {
  return (
    <Alert
      severity={validation.valid ? 'success' : 'error'}
      icon={validation.valid ? <CheckCircleIcon fontSize="small" /> : <ErrorIcon fontSize="small" />}
      sx={{ mt: 1.25, fontSize: 11.5, '& .MuiAlert-message': { width: '100%' } }}
    >
      {validation.valid ? (
        <>
          <strong>SQL is valid.</strong>
          {validation.detectedColumns?.length > 0 && (
            <Box sx={{ mt: 0.5 }}>
              Detected columns:{' '}
              {validation.detectedColumns.map(c =>
                <Chip key={c} label={c} size="small" sx={{ height: 18, fontSize: 10, mr: 0.5, mt: 0.25 }} />)}
            </Box>
          )}
        </>
      ) : (
        <>
          <strong>Validation failed:</strong>
          <ul style={{ margin: '4px 0 0 0', paddingLeft: 18 }}>
            {(validation.errors ?? []).map((err, i) => <li key={i}>{err}</li>)}
          </ul>
          {validation.missingColumns?.length > 0 && (
            <Box sx={{ mt: 0.5 }}>
              Missing in view:{' '}
              {validation.missingColumns.map(c =>
                <Chip key={c} label={c} size="small" color="error" sx={{ height: 18, fontSize: 10, mr: 0.5, mt: 0.25 }} />)}
            </Box>
          )}
        </>
      )}
    </Alert>
  )
}